import { useState, useEffect } from "react"
import { useForm, type Resolver } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { z } from "zod"
import { useQuery, useMutation } from "urql"
import { useDebouncedValue } from "@/hooks/use-debounced-value"
import { useCursorPagination } from "@/hooks/use-cursor-pagination"
import { Plus, Pencil, Trash2, Loader2 } from "lucide-react"
import { toast } from "sonner"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Switch } from "@/components/ui/switch"
import { Badge } from "@/components/ui/badge"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { PageHeader } from "@/components/shared/page-header"
import { DataTable, type Column } from "@/components/shared/data-table"
import { TimeAgo } from "@/components/shared/time-ago"
import { ConfirmDialog } from "@/components/shared/confirm-dialog"
import { FieldError } from "@/components/shared/field-error"
import { MetaEventConfigsQuery } from "@/lib/graphql/queries"
import {
  CreateMetaEventConfigMutation,
  UpdateMetaEventConfigMutation,
  DeleteMetaEventConfigMutation,
} from "@/lib/graphql/mutations"
import type { ResultOf } from "@/lib/graphql/types"

type MetaEventConfigItem = ResultOf<typeof MetaEventConfigsQuery>["metaEventConfigs"]["nodes"][number]

const META_EVENT_TYPES = [
  "endpoint.created",
  "endpoint.disabled",
  "delivery.exhausted",
  "dlq.entry_created",
  "message.sent",
] as const

const metaEventConfigSchema = z.object({
  name: z.string().min(1, "Name is required").max(255),
  url: z.string().url("Must be a valid URL"),
  eventTypes: z.string().min(1, "At least one event type is required"),
  enabled: z.boolean().optional(),
})

type MetaEventConfigFormValues = z.infer<typeof metaEventConfigSchema>

const LIMIT = 25

export function MetaEventsPage() {
  const [searchInput, setSearchInput] = useState("")
  const debouncedSearch = useDebouncedValue(searchInput)
  const search = debouncedSearch || undefined
  const pagination = useCursorPagination(LIMIT, [search])

  const [result, reexecute] = useQuery({
    query: MetaEventConfigsQuery,
    variables: { search, after: pagination.after, first: pagination.first },
  })
  const [, deleteMeta] = useMutation(DeleteMetaEventConfigMutation)

  const [createOpen, setCreateOpen] = useState(false)
  const [editItem, setEditItem] = useState<MetaEventConfigItem | null>(null)
  const [deleteId, setDeleteId] = useState<string | null>(null)

  const handleDelete = async () => {
    if (!deleteId) return
    const res = await deleteMeta({ id: deleteId })
    if (res.error) { toast.error(res.error.message); return }
    toast.success("Meta event config deleted")
    setDeleteId(null)
    reexecute({ requestPolicy: "network-only" })
  }

  const columns: Column<MetaEventConfigItem>[] = [
    {
      header: "Name",
      cell: (row) => <span className="text-xs font-medium">{row.name}</span>,
    },
    {
      header: "URL",
      cell: (row) => (
        <code className="text-xs font-mono text-muted-foreground max-w-60 truncate block">
          {row.url}
        </code>
      ),
    },
    {
      header: "Event Types",
      cell: (row) => (
        <div className="flex flex-wrap gap-1">
          {row.eventTypes.map((et) => (
            <Badge key={et} variant="secondary" className="text-2xs">{et}</Badge>
          ))}
        </div>
      ),
    },
    {
      header: "Status",
      cell: (row) => (
        <Badge variant={row.enabled ? "default" : "secondary"} className="text-2xs">
          {row.enabled ? "Enabled" : "Disabled"}
        </Badge>
      ),
    },
    {
      header: "Created",
      cell: (row) => <TimeAgo date={row.createdAt} />,
    },
    {
      header: "",
      cell: (row) => (
        <div className="flex gap-1">
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={(e) => { e.stopPropagation(); setEditItem(row) }}
            aria-label="Edit"
          >
            <Pencil className="h-3.5 w-3.5" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={(e) => { e.stopPropagation(); setDeleteId(row.id) }}
            aria-label="Delete"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      ),
    },
  ]

  return (
    <div className="space-y-6">
      <PageHeader title="Meta Events" description="Receive webhook notifications about system events">
        <Button size="sm" onClick={() => setCreateOpen(true)} className="shadow-primary-glow transition-shadow">
          <Plus className="h-3.5 w-3.5 mr-1.5" />
          Create Meta Event
        </Button>
      </PageHeader>

      <div className="flex items-center gap-3 rounded-lg glass-card p-3">
        <Input
          placeholder="Search meta events..."
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          className="w-50 text-sm"
        />
      </div>

      <DataTable
        columns={columns}
        data={result.data?.metaEventConfigs.nodes ?? []}
        keyFn={(row) => row.id}
        cursorInfo={result.data?.metaEventConfigs.pageInfo}
        cursorCallbacks={pagination}
        pageSize={LIMIT}
        loading={result.fetching}
        emptyTitle="No meta event configs"
        emptyDescription="Configure webhooks to receive notifications about system events"
      />

      <MetaEventConfigForm
        open={createOpen}
        onOpenChange={setCreateOpen}
        onSuccess={() => reexecute({ requestPolicy: "network-only" })}
      />

      {editItem && (
        <MetaEventConfigForm
          open
          onOpenChange={() => setEditItem(null)}
          onSuccess={() => { setEditItem(null); reexecute({ requestPolicy: "network-only" }) }}
          initialData={editItem}
        />
      )}

      <ConfirmDialog
        open={!!deleteId}
        onOpenChange={(open) => { if (!open) setDeleteId(null) }}
        title="Delete Meta Event Config"
        description="This will permanently delete this meta event configuration. You will no longer receive notifications for these events."
        confirmLabel="Delete"
        destructive
        onConfirm={handleDelete}
      />
    </div>
  )
}

interface MetaEventConfigFormProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSuccess: () => void
  initialData?: MetaEventConfigItem
}

function MetaEventConfigForm({ open, onOpenChange, onSuccess, initialData }: MetaEventConfigFormProps) {
  const isEdit = !!initialData
  const [, create] = useMutation(CreateMetaEventConfigMutation)
  const [, update] = useMutation(UpdateMetaEventConfigMutation)

  const form = useForm<MetaEventConfigFormValues>({
    resolver: zodResolver(metaEventConfigSchema) as Resolver<MetaEventConfigFormValues>,
    defaultValues: {
      name: initialData?.name ?? "",
      url: initialData?.url ?? "",
      eventTypes: initialData?.eventTypes.join(", ") ?? "",
      enabled: initialData?.enabled ?? true,
    },
  })

  useEffect(() => {
    if (open) {
      form.reset({
        name: initialData?.name ?? "",
        url: initialData?.url ?? "",
        eventTypes: initialData?.eventTypes.join(", ") ?? "",
        enabled: initialData?.enabled ?? true,
      })
    }
  }, [open, initialData, form])

  const onSubmit = async (values: MetaEventConfigFormValues) => {
    const eventTypes = values.eventTypes
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean)

    if (isEdit) {
      const result = await update({
        id: initialData.id,
        input: {
          name: values.name,
          url: values.url,
          eventTypes,
          enabled: values.enabled,
        },
      })
      if (result.error) { toast.error(result.error.message); return }
      toast.success("Meta event config updated")
    } else {
      const result = await create({
        input: {
          name: values.name,
          url: values.url,
          eventTypes,
          enabled: values.enabled,
        },
      })
      if (result.error) { toast.error(result.error.message); return }
      toast.success("Meta event config created")
    }

    onSuccess()
    onOpenChange(false)
    form.reset()
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit" : "Create"} Meta Event Config</DialogTitle>
        </DialogHeader>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <div className="space-y-4 rounded-lg glass-card p-4">
            <div className="space-y-2">
              <Label htmlFor="meta-name">Name</Label>
              <Input id="meta-name" placeholder="DLQ notifications" {...form.register("name")} />
              <FieldError error={form.formState.errors.name} />
            </div>

            <div className="space-y-2">
              <Label htmlFor="meta-url">URL</Label>
              <Input
                id="meta-url"
                className="font-mono text-sm"
                placeholder="https://api.example.com/webhooks/meta"
                {...form.register("url")}
              />
              <FieldError error={form.formState.errors.url} />
            </div>

            <div className="space-y-2">
              <Label htmlFor="meta-eventTypes">Event Types (comma-separated)</Label>
              <Input
                id="meta-eventTypes"
                className="font-mono text-sm"
                placeholder="endpoint.created, delivery.exhausted"
                {...form.register("eventTypes")}
              />
              <FieldError error={form.formState.errors.eventTypes} />
              <p className="text-xs text-muted-foreground">
                Available: {META_EVENT_TYPES.join(", ")}
              </p>
            </div>

            <div className="flex items-center justify-between">
              <Label htmlFor="meta-enabled">Enabled</Label>
              <Switch
                id="meta-enabled"
                checked={form.watch("enabled") ?? true}
                onCheckedChange={(checked) => form.setValue("enabled", checked)}
              />
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={form.formState.isSubmitting}>
              {form.formState.isSubmitting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : isEdit ? "Update" : "Create"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
